import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronDown } from "lucide-react";
import { Arrow } from "@/components/icons";
import { routeHead } from "@/components/seo";
import { pageMeta, site } from "@/content";
import data from "@/content/site.json";

const ROUTE = "/frequently-asked-questions";
const faqs = data.faqs;

export const Route = createFileRoute("/frequently-asked-questions")({
  head: () =>
    routeHead(ROUTE, {
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.question,
          acceptedAnswer: { "@type": "Answer", text: f.answer },
        })),
      },
    }),
  component: Page,
});

function Page() {
  const p = pageMeta(ROUTE)!;
  const [open, setOpen] = useState<number | null>(0);
  return (
    <div className="bg-background text-foreground">
      <section className="border-b border-line">
        <div className="mx-auto max-w-4xl px-6 py-20">
          <h1 className="hero-enter mt-4 font-serif text-4xl leading-[1.1] text-ink sm:text-5xl">
            {p.h1}
          </h1>
          <p className="hero-enter-delay mt-6 text-lg leading-relaxed text-ink/80">
            {p.description}
          </p>

          <div className="stagger mt-12 divide-y divide-line border-y border-line">
            {faqs.map((f, i) => {
              const isOpen = open === i;
              return (
                <div key={f.question}>
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? null : i)}
                    aria-expanded={isOpen}
                    className="flex w-full items-center justify-between gap-6 py-5 text-left"
                  >
                    <span className="font-serif text-lg text-ink">{f.question}</span>
                    <ChevronDown
                      className={`h-5 w-5 shrink-0 text-teal transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                      aria-hidden
                    />
                  </button>
                  <p
                    className={`grid overflow-hidden leading-relaxed text-ink/80 transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                      isOpen ? "grid-rows-[1fr] pb-6 pr-10 opacity-100" : "grid-rows-[0fr] opacity-0"
                    }`}
                    style={{ display: "grid" }}
                  >
                    <span className="min-h-0">{f.answer}</span>
                  </p>
                </div>
              );
            })}
          </div>

          <div className="reveal mt-14">
            <Link to={site.primaryCta.href} className="btn-primary">
              {site.primaryCta.label}
              <Arrow className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
